import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Duel, Membership, Notification, User } from '@h2h/shared';
import { db } from '../../lib/firebase';
import {
  addFriend,
  blockUser,
  cancelChallenge,
  challengeFriend,
  getDuel,
  getFixtureInsight,
  getPlayerRound,
  removeFriend,
  reportUser,
  respondToChallenge,
  unblockUser,
  type BlockedUser,
  type ReportUserInput
} from '../../lib/api';
import { FIVE_MIN, LIVE_POLL, LIVE_POLL_FAST } from '../firestore/live';

// ── Friends: season leaderboard (vanity seasonPoints) ─────────────────────────-

export interface FriendSeasonRow {
  userId: string;
  displayName: string;
  avatarTone: number;
  photoURL?: string;
  tier: number;
  seasonPoints: number;
  isYou: boolean;
}

/**
 * The user + their friends who have joined a league, ranked by vanity season
 * points (the friends leaderboard). Season points are display-only and never gate
 * the real ladder — tier is shown separately, straight off the membership.
 */
export function useFriendsSeason(uid: string | null, leagueId: string | undefined) {
  return useQuery({
    queryKey: ['friendsSeason', uid, leagueId],
    enabled: !!uid && !!leagueId,
    queryFn: async (): Promise<FriendSeasonRow[]> => {
      const meSnap = await getDoc(doc(db, 'users', uid!));
      const me = meSnap.exists() ? (meSnap.data() as User) : null;
      const ids = [...new Set([uid!, ...(me?.friendIds ?? [])])];

      const rows = await Promise.all(
        ids.map(async (id) => {
          const [mSnap, uSnap] = await Promise.all([
            getDoc(doc(db, 'memberships', `${id}_${leagueId}`)),
            getDoc(doc(db, 'users', id))
          ]);
          if (!mSnap.exists()) return null;
          const m = mSnap.data() as Membership;
          const u = uSnap.exists() ? (uSnap.data() as User) : null;
          return {
            userId: id,
            displayName: u?.displayName ?? 'Player',
            avatarTone: u?.avatarTone ?? 0,
            photoURL: u?.photoURL,
            tier: m.tier,
            seasonPoints: m.seasonPoints ?? 0,
            isYou: id === uid
          } as FriendSeasonRow;
        })
      );

      return rows
        .filter((r): r is FriendSeasonRow => r != null)
        .sort((a, b) => b.seasonPoints - a.seasonPoints || b.tier - a.tier);
    }
  });
}
// ── Duels ─────────────────────────────────────────────────────────────────────

/** Every duel the user is in (optionally scoped to a league), newest first. */
export function useMyDuels(uid: string | null, leagueId?: string) {
  return useQuery({
    queryKey: ['duels', uid, leagueId ?? null],
    enabled: !!uid,
    refetchInterval: LIVE_POLL,
    queryFn: async (): Promise<Duel[]> => {
      const q = query(collection(db, 'duels'), where('participantIds', 'array-contains', uid));
      const snap = await getDocs(q);
      let duels = snap.docs.map((d) => d.data() as Duel);
      if (leagueId) duels = duels.filter((d) => d.leagueId === leagueId);
      return duels.sort((a, b) => b.createdAt - a.createdAt);
    }
  });
}

/** A single duel's full pick-by-pick detail (callable, kickoff-gated reveal). */
export function useDuel(duelId: string | undefined, live = false) {
  return useQuery({
    queryKey: ['duel', duelId],
    enabled: !!duelId,
    refetchInterval: live ? LIVE_POLL_FAST : false,
    queryFn: () => getDuel(duelId!)
  });
}

/**
 * Another player's picks for a round, for their profile (callable, kickoff-gated
 * reveal). Defaults to their most recently entered round when `roundId` is omitted.
 */
export function usePlayerRound(
  leagueId: string | undefined,
  userId: string | null | undefined,
  roundId?: string | null
) {
  return useQuery({
    queryKey: ['playerRound', leagueId, userId, roundId ?? null],
    enabled: !!leagueId && !!userId,
    queryFn: () => getPlayerRound(leagueId!, userId!, roundId ?? undefined),
    // Keep the current card on screen while stepping to another round (history switcher),
    // so it cross-fades instead of flashing the skeleton.
    placeholderData: (prev) => prev,
    // Poll while the round is in motion so a rival's card stays live: hidden picks
    // reveal as each fixture kicks off (lockedCount counts down), and live scores /
    // projected points tick. Faster while a fixture is actually LIVE; slower while we're
    // only waiting on the next kickoff; fully static once it's settled with nothing left
    // to reveal (so a finished card stops hitting the callable).
    refetchInterval: (query) => {
      const detail = query.state.data;
      if (!detail?.round) return false;
      if (detail.round.status === 'SETTLED') return false; // terminal — the card is final
      if (detail.fixtures.some((f) => f.status === 'LIVE')) return LIVE_POLL_FAST;
      const inMotion =
        detail.player.lockedCount > 0 ||
        detail.round.status === 'OPEN' ||
        detail.round.status === 'LOCKING' ||
        detail.round.status === 'SETTLING';
      return inMotion ? LIVE_POLL : false;
    }
  });
}

/** Display names + avatar tones for a set of users (for duel/challenge cards). */
export function useUsers(ids: string[]) {
  const sorted = [...new Set(ids)].sort();
  return useQuery({
    queryKey: ['users', sorted],
    enabled: sorted.length > 0,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<Record<string, User>> => {
      const snaps = await Promise.all(sorted.map((id) => getDoc(doc(db, 'users', id))));
      const out: Record<string, User> = {};
      snaps.forEach((s) => {
        if (s.exists()) out[s.id] = s.data() as User;
      });
      return out;
    }
  });
}

/** A single public user profile (display name, avatar, username, friends). */
export function useUser(userId: string | null | undefined) {
  return useQuery({
    queryKey: ['user', userId],
    enabled: !!userId,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<User | null> => {
      const snap = await getDoc(doc(db, 'users', userId!));
      return snap.exists() ? (snap.data() as User) : null;
    }
  });
}
// ── Notifications ──────────────────────────────────────────────────────────────

/** The user's activity feed, newest first. Polls so new items appear live. */
export function useNotifications(uid: string | null) {
  return useQuery({
    queryKey: ['notifications', uid],
    enabled: !!uid,
    refetchInterval: LIVE_POLL,
    queryFn: async (): Promise<Notification[]> => {
      const q = query(
        collection(db, 'users', uid!, 'notifications'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => d.data() as Notification);
    }
  });
}

/**
 * Unread notification counts grouped by league, derived from the single inbox
 * fetch above. Powers the per-league bell badge plus the cross-league "news in
 * another world" dots on the lobby and league switcher. (Leagueless notifications
 * are ignored — every notification the engine emits today carries a leagueId.)
 */
export function useUnreadByLeague(uid: string | null): Record<string, number> {
  const { data: notifications } = useNotifications(uid);
  return useMemo(() => {
    const counts: Record<string, number> = {};
    for (const n of notifications ?? []) {
      if (n.readAt != null) continue;
      const lid = n.data?.leagueId;
      if (!lid) continue;
      counts[lid] = (counts[lid] ?? 0) + 1;
    }
    return counts;
  }, [notifications]);
}

// ── Community insight (Match detail) ──────────────────────────────────────────-

export function useFixtureInsight(
  leagueId: string | undefined,
  roundId: string | null | undefined,
  fixtureId: string | undefined
) {
  return useQuery({
    queryKey: ['insight', leagueId, roundId, fixtureId],
    enabled: !!leagueId && !!roundId && !!fixtureId,
    staleTime: 60 * 1000,
    queryFn: () => getFixtureInsight(leagueId!, roundId!, fixtureId!)
  });
}
export function useAddFriend(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId?: string; username?: string }) => addFriend(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', uid] });
      qc.invalidateQueries({ queryKey: ['friendsSeason'] });
      qc.invalidateQueries({ queryKey: ['friendsInLeague'] });
    }
  });
}

export function useRemoveFriend(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (friendId: string) => removeFriend(friendId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', uid] });
      qc.invalidateQueries({ queryKey: ['friendsSeason'] });
      qc.invalidateQueries({ queryKey: ['friendsInLeague'] });
    }
  });
}

export function useChallengeFriend(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { leagueId: string; opponentId: string; roundId?: string }) =>
      challengeFriend(input.leagueId, input.opponentId, input.roundId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['duels', uid] })
  });
}

// ── Moderation & safety (block / unblock / report) ─────────────────────────────-

/** The set of userIds the signed-in user has blocked (live read of own blocks). */
export function useBlockedUsers(uid: string | null) {
  return useQuery({
    queryKey: ['blocks', uid],
    enabled: !!uid,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<BlockedUser[]> => {
      const snap = await getDocs(collection(db, 'users', uid!, 'blocks'));
      return snap.docs.map((d) => d.data() as BlockedUser);
    }
  });
}

/** Convenience: just the blocked id set, for filtering lists. */
export function useBlockedIds(uid: string | null): Set<string> {
  const { data } = useBlockedUsers(uid);
  return useMemo(() => new Set((data ?? []).map((b) => b.userId)), [data]);
}

export function useBlockUser(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId?: string; username?: string }) => blockUser(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blocks', uid] });
      qc.invalidateQueries({ queryKey: ['user', uid] });
      qc.invalidateQueries({ queryKey: ['friendsSeason'] });
      qc.invalidateQueries({ queryKey: ['friendsInLeague'] });
      qc.invalidateQueries({ queryKey: ['duels', uid] });
    }
  });
}

export function useUnblockUser(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => unblockUser(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blocks', uid] })
  });
}

export function useReportUser() {
  return useMutation({ mutationFn: (input: ReportUserInput) => reportUser(input) });
}

export function useRespondToChallenge(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { duelId: string; accept: boolean }) =>
      respondToChallenge(input.duelId, input.accept),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['duels', uid] });
      qc.invalidateQueries({ queryKey: ['duel', vars.duelId] });
    }
  });
}

export function useCancelChallenge(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (duelId: string) => cancelChallenge(duelId),
    onSuccess: (_d, duelId) => {
      qc.invalidateQueries({ queryKey: ['duels', uid] });
      qc.invalidateQueries({ queryKey: ['duel', duelId] });
    }
  });
}
